import React, { useState } from 'react';
import { Box, IconButton, Snackbar, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

/**
 * Copyable UUID shown under a resource name. Displays the full UUID and a copy
 * button; clicking copies it and briefly shows a confirmation. The id stays on
 * a single line and is truncated with an ellipsis when the column is too narrow
 * (hover shows the full value; the copy button always copies the whole UUID).
 *
 * Meant for letting users grab a glossary / reference store UUID to pass to the
 * external STANDARD translate API.
 */
export const CopyableId: React.FC<{ id?: string | null }> = ({ id }) => {
  const [copied, setCopied] = useState(false);
  if (!id) return null;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
    } catch {
      // Fallback for browsers without the async clipboard API
      const ta = document.createElement('textarea');
      ta.value = id;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); } catch {}
      document.body.removeChild(ta);
    }
  };

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25, minWidth: 0 }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Tooltip title={id} placement="top">
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontFamily: 'monospace',
            cursor: 'default',
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {id}
        </Typography>
      </Tooltip>
      <Tooltip title="Copy ID" placement="top">
        <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25, flexShrink: 0 }}>
          {copied
            ? <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
            : <ContentCopyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
        </IconButton>
      </Tooltip>
      <Snackbar
        open={copied}
        autoHideDuration={1500}
        onClose={() => setCopied(false)}
        message="ID copied"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
